import React, {
  useEffect, useRef, useCallback,
  useImperativeHandle, forwardRef, useState,
} from 'react';
import { drawGlobe, initStars, initClouds, project } from '../utils/globe';

const CESIUM_VERSION = '1.116';
const CESIUM_CDN     = `https://cesium.com/downloads/cesiumjs/releases/${CESIUM_VERSION}/Build/Cesium`;
const CESIUM_TOKEN   = process.env.REACT_APP_CESIUM_TOKEN || '';

// ══════════════════════════════════════════════════════════════════════════════
// CANVAS 2D FALLBACK
// ══════════════════════════════════════════════════════════════════════════════
function Canvas2DGlobe({ sightings, predictions, selectedId, onSelect, onZoomLevel, globeRef }) {
  const canvasRef = useRef(null);
  const stateRef  = useRef({
    rotY:-1.6, rotX:0.32, zoom:1.0, targetZoom:1.0,
    targetRotY:-1.6, targetRotX:0.32, animating:false,
    autoRotate:true, drag:false, lastX:0, lastY:0, lastDist:0,
    time:0, stars:initStars(800), clouds:initClouds(50),
    currentLat:0, currentLng:0,
  });
  const animRef = useRef(null);
  const getR = useCallback((W,H,z) => Math.min(W,H)*0.41*Math.max(0.7,Math.min(z,18)),[]);

  useImperativeHandle(globeRef, () => ({
    zoomTo(lat,lng,z=5) {
      const s=stateRef.current;
      s.targetRotY=-(lng*Math.PI/180);
      s.targetRotX=Math.max(-1.1,Math.min(1.1,((90-lat)*Math.PI/180-Math.PI/2)*0.65));
      // Gentle zoom — level 5 keeps comfortable overview distance
      s.targetZoom = z >= 8 ? 2.5 : z >= 5 ? 1.8 : 1.2;
      s.autoRotate=false; s.animating=true;
      s.currentLat=lat; s.currentLng=lng;
    },
    resetView() {
      const s=stateRef.current;
      s.targetRotY=-1.6; s.targetRotX=0.32; s.targetZoom=1.0;
      s.autoRotate=true; s.animating=true;
    },
    getCurrentZoom() { return stateRef.current.zoom; },
    showPredictionPath() {}, // no-op for 2D
  }));

  const hitTest=useCallback((mx,my)=>{
    const canvas=canvasRef.current; if(!canvas||!sightings) return null;
    const s=stateRef.current,W=canvas.offsetWidth,H=canvas.offsetHeight,R=getR(W,H,s.zoom);
    for(const inc of sightings){
      const p=project(inc.lat,inc.lng,s.rotY,s.rotX,W,H,R);
      if(p.visible&&Math.sqrt((mx-p.x)**2+(my-p.y)**2)<22) return inc;
    }
    return null;
  },[sightings,getR]);

  useEffect(()=>{
    const canvas=canvasRef.current; if(!canvas) return;
    const ctx=canvas.getContext('2d'),dpr=window.devicePixelRatio||1;
    const resize=()=>{canvas.width=canvas.offsetWidth*dpr;canvas.height=canvas.offsetHeight*dpr;ctx.scale(dpr,dpr);};
    resize();
    const ro=new ResizeObserver(resize); ro.observe(canvas);
    let lastZ=1;
    const render=()=>{
      const s=stateRef.current,W=canvas.offsetWidth,H=canvas.offsetHeight,R=getR(W,H,s.zoom);
      if(s.animating){
        const spd=s.zoom>3?0.04:0.07;
        s.rotY+=(s.targetRotY-s.rotY)*spd; s.rotX+=(s.targetRotX-s.rotX)*spd; s.zoom+=(s.targetZoom-s.zoom)*spd;
        if(Math.abs(s.targetRotY-s.rotY)<0.0005&&Math.abs(s.targetRotX-s.rotX)<0.0005&&Math.abs(s.targetZoom-s.zoom)<0.001)
          {s.rotY=s.targetRotY;s.rotX=s.targetRotX;s.zoom=s.targetZoom;s.animating=false;}
      }
      if(onZoomLevel&&Math.abs(s.zoom-lastZ)>0.4){lastZ=s.zoom;onZoomLevel(s.zoom,s.currentLat,s.currentLng);}
      drawGlobe(ctx,s,W,H,R,sightings,predictions,selectedId);
      if(s.autoRotate) s.rotY-=0.0007;
      s.time+=0.006;
      animRef.current=requestAnimationFrame(render);
    };
    render();
    return()=>{cancelAnimationFrame(animRef.current);ro.disconnect();};
  },[sightings,predictions,selectedId,getR,onZoomLevel]);

  useEffect(()=>{
    const canvas=canvasRef.current; if(!canvas) return;
    const s=stateRef.current;
    const onDown=e=>{s.drag=true;s.lastX=e.clientX;s.lastY=e.clientY;s.autoRotate=false;};
    const onMove=e=>{if(!s.drag)return;s.rotY+=(e.clientX-s.lastX)*0.005;s.rotX=Math.max(-1.15,Math.min(1.15,s.rotX-(e.clientY-s.lastY)*0.005));s.lastX=e.clientX;s.lastY=e.clientY;};
    const onUp=()=>{s.drag=false;};
    const onClick=e=>{const r=canvas.getBoundingClientRect();onSelect(hitTest(e.clientX-r.left,e.clientY-r.top));};
    const onWheel=e=>{e.preventDefault();s.zoom=Math.max(0.7,Math.min(18,s.zoom-e.deltaY*0.001));s.targetZoom=s.zoom;s.autoRotate=false;if(onZoomLevel)onZoomLevel(s.zoom,s.currentLat,s.currentLng);};
    const onTS=e=>{if(e.touches.length===1){s.drag=true;s.lastX=e.touches[0].clientX;s.lastY=e.touches[0].clientY;s.autoRotate=false;}else if(e.touches.length===2){s.drag=false;const dx=e.touches[0].clientX-e.touches[1].clientX;const dy=e.touches[0].clientY-e.touches[1].clientY;s.lastDist=Math.sqrt(dx*dx+dy*dy);}};
    const onTM=e=>{e.preventDefault();if(e.touches.length===1&&s.drag){s.rotY+=(e.touches[0].clientX-s.lastX)*0.006;s.rotX=Math.max(-1.15,Math.min(1.15,s.rotX-(e.touches[0].clientY-s.lastY)*0.006));s.lastX=e.touches[0].clientX;s.lastY=e.touches[0].clientY;}else if(e.touches.length===2){const dx=e.touches[0].clientX-e.touches[1].clientX;const dy=e.touches[0].clientY-e.touches[1].clientY;const dist=Math.sqrt(dx*dx+dy*dy);s.zoom=Math.max(0.7,Math.min(18,s.zoom+(dist-s.lastDist)*0.007));s.targetZoom=s.zoom;s.lastDist=dist;if(onZoomLevel)onZoomLevel(s.zoom,s.currentLat,s.currentLng);}};
    const onTE=e=>{s.drag=false;if(e.changedTouches.length===1){const r=canvas.getBoundingClientRect();const t=e.changedTouches[0];const hit=hitTest(t.clientX-r.left,t.clientY-r.top);if(hit)onSelect(hit);}};
    canvas.addEventListener('mousedown',onDown);window.addEventListener('mousemove',onMove);window.addEventListener('mouseup',onUp);
    canvas.addEventListener('click',onClick);canvas.addEventListener('wheel',onWheel,{passive:false});
    canvas.addEventListener('touchstart',onTS,{passive:false});canvas.addEventListener('touchmove',onTM,{passive:false});canvas.addEventListener('touchend',onTE);
    return()=>{canvas.removeEventListener('mousedown',onDown);window.removeEventListener('mousemove',onMove);window.removeEventListener('mouseup',onUp);canvas.removeEventListener('click',onClick);canvas.removeEventListener('wheel',onWheel);canvas.removeEventListener('touchstart',onTS);canvas.removeEventListener('touchmove',onTM);canvas.removeEventListener('touchend',onTE);};
  },[hitTest,onSelect,onZoomLevel]);

  return <canvas ref={canvasRef} style={{width:'100%',height:'100%',display:'block',cursor:'crosshair',touchAction:'none'}}/>;
}

// ══════════════════════════════════════════════════════════════════════════════
// CESIUM GLOBE
// ══════════════════════════════════════════════════════════════════════════════
function CesiumGlobe({ sightings, predictions, selectedId, onSelect, onZoomLevel, globeRef, onError }) {
  const containerRef = useRef(null);
  const viewerRef    = useRef(null);

  useImperativeHandle(globeRef, () => ({

    // ── Fly to location ───────────────────────────────────────────────────
    zoomTo(lat, lng, zoomLevel = 5) {
      const viewer = viewerRef.current;
      if (!viewer || viewer.isDestroyed()) return;
      const Cesium = window.Cesium;

      // Single smooth fly — no double animation, no jarring second move
      // Keep at comfortable viewing altitude — never too close
      const altMeters = zoomLevel >= 10 ? 180000   // city level — comfortable
                      : zoomLevel >= 8  ? 350000    // regional
                      : zoomLevel >= 6  ? 800000    // country
                      : zoomLevel >= 4  ? 2500000   // continental
                      :                   8000000;  // global

      viewer.camera.flyTo({
        destination: Cesium.Cartesian3.fromDegrees(lng, lat, altMeters),
        orientation: {
          heading: 0,
          pitch:   Cesium.Math.toRadians(-55),
          roll:    0,
        },
        duration:       2.2,
        easingFunction: Cesium.EasingFunction.CUBIC_IN_OUT,
        // NO complete callback — single smooth move only
      });
    },

    // ── Reset to space view ───────────────────────────────────────────────
    resetView() {
      const viewer = viewerRef.current;
      if (!viewer || viewer.isDestroyed()) return;
      const Cesium = window.Cesium;
      viewer.camera.flyTo({
        destination: Cesium.Cartesian3.fromDegrees(-40, 20, 22000000),
        orientation: { heading:0, pitch:Cesium.Math.toRadians(-90), roll:0 },
        duration: 2.0,
      });
    },

    getCurrentZoom() { return 1; },

    // ── CINEMATIC PREDICTION PATH — Multi-layer AI route visualization ──────
    showPredictionPath(preds) {
      const viewer = viewerRef.current;
      if (!viewer || viewer.isDestroyed() || !window.Cesium) return;
      const Cesium = window.Cesium;

      const toRemove = [];
      viewer.entities.values.forEach(e => { if (e._isPredPath) toRemove.push(e); });
      toRemove.forEach(e => viewer.entities.remove(e));
      if (!preds || preds.length < 2) return;

      const tag = (ent) => { ent._isPredPath = true; return ent; };

      // Layer 1: Wide glow corridor
      tag(viewer.entities.add({ polyline: {
        positions: preds.map(p => Cesium.Cartesian3.fromDegrees(p.lng, p.lat, 120000)),
        width: 18, arcType: Cesium.ArcType.GEODESIC,
        material: new Cesium.PolylineGlowMaterialProperty({
          glowPower: 0.3, color: Cesium.Color.fromCssColorString('#1de050').withAlpha(0.08),
        }),
      }}));

      // Layer 2: Amber probability corridor
      tag(viewer.entities.add({ polyline: {
        positions: preds.map(p => Cesium.Cartesian3.fromDegrees(p.lng, p.lat, 90000)),
        width: 6, arcType: Cesium.ArcType.GEODESIC,
        material: new Cesium.PolylineDashMaterialProperty({
          color: Cesium.Color.fromCssColorString('#ffaa00').withAlpha(0.5),
          dashLength: 32, dashPattern: 0xFF00,
        }),
      }}));

      // Layer 3: Core glowing route
      tag(viewer.entities.add({ polyline: {
        positions: preds.map(p => Cesium.Cartesian3.fromDegrees(p.lng, p.lat, 60000)),
        width: 3, arcType: Cesium.ArcType.GEODESIC,
        material: new Cesium.PolylineGlowMaterialProperty({
          glowPower: 0.6, color: Cesium.Color.fromCssColorString('#1de050').withAlpha(0.95),
        }),
      }}));

      // Layer 4: Vertical pillars + rings at each node
      preds.forEach((p, i) => {
        tag(viewer.entities.add({ polyline: {
          positions: [Cesium.Cartesian3.fromDegrees(p.lng,p.lat,0), Cesium.Cartesian3.fromDegrees(p.lng,p.lat,80000)],
          width: 2, material: new Cesium.PolylineDashMaterialProperty({
            color: Cesium.Color.fromCssColorString('#1de050').withAlpha(0.4), dashLength: 16,
          }),
        }}));
        tag(viewer.entities.add({ position: Cesium.Cartesian3.fromDegrees(p.lng,p.lat,500),
          ellipse: { semiMinorAxis:120000, semiMajorAxis:120000,
            material: Cesium.Color.fromCssColorString('#1de050').withAlpha(0.05),
            outline:true, outlineColor: Cesium.Color.fromCssColorString('#1de050').withAlpha(0.4), outlineWidth:2,
          },
        }));
        tag(viewer.entities.add({ position: Cesium.Cartesian3.fromDegrees(p.lng,p.lat,500),
          ellipse: { semiMinorAxis:55000, semiMajorAxis:55000,
            material: Cesium.Color.fromCssColorString('#ffaa00').withAlpha(0.04),
            outline:true, outlineColor: Cesium.Color.fromCssColorString('#ffaa00').withAlpha(0.3), outlineWidth:1,
          },
        }));
        if (p.region || p.tracking) {
          tag(viewer.entities.add({ position: Cesium.Cartesian3.fromDegrees(p.lng,p.lat,90000),
            label: {
              text: `${i+1}. ${p.region||p.tracking||'PRED ZONE'}\n${p.confidence?p.confidence+'% CONF':''}`,
              font:'11px monospace', fillColor: Cesium.Color.fromCssColorString('#1de050'),
              outlineColor: Cesium.Color.BLACK, outlineWidth:2,
              style: Cesium.LabelStyle.FILL_AND_OUTLINE,
              verticalOrigin: Cesium.VerticalOrigin.BOTTOM,
              pixelOffset: new Cesium.Cartesian2(0,-20),
              disableDepthTestDistance: Number.POSITIVE_INFINITY,
              distanceDisplayCondition: new Cesium.DistanceDisplayCondition(0, 8000000),
            },
          }));
        }
      });

      // Layer 5: Animated UFO — smooth Hermite interpolation
      const secsPerLeg = 6;
      const startTime  = Cesium.JulianDate.now();
      const stopTime   = Cesium.JulianDate.addSeconds(startTime, preds.length*secsPerLeg, new Cesium.JulianDate());
      const sampled    = new Cesium.SampledPositionProperty();
      sampled.setInterpolationOptions({ interpolationDegree:3, interpolationAlgorithm:Cesium.HermitePolynomialApproximation });
      preds.forEach((p,i) => {
        const alt = 60000 + Math.sin(i*1.2)*20000;
        const t   = Cesium.JulianDate.addSeconds(startTime, i*secsPerLeg, new Cesium.JulianDate());
        sampled.addSample(t, Cesium.Cartesian3.fromDegrees(p.lng, p.lat, alt));
      });
      tag(viewer.entities.add({
        availability: new Cesium.TimeIntervalCollection([new Cesium.TimeInterval({start:startTime,stop:stopTime})]),
        position: sampled,
        billboard: { image:createUFOIcon(), width:64, height:32,
          verticalOrigin: Cesium.VerticalOrigin.BOTTOM,
          disableDepthTestDistance: Number.POSITIVE_INFINITY,
          scaleByDistance: new Cesium.NearFarScalar(1e3,1.5,2e7,0.5),
        },
        path: { resolution:1, width:4, leadTime:0, trailTime:20,
          material: new Cesium.PolylineGlowMaterialProperty({
            glowPower:0.4, color: Cesium.Color.fromCssColorString('#1de050').withAlpha(0.7),
          }),
        },
      }));

      // Layer 6: Ghost UFO trailing 8s behind
      const ghostStart = Cesium.JulianDate.addSeconds(startTime, -8, new Cesium.JulianDate());
      const ghostStop  = Cesium.JulianDate.addSeconds(stopTime,  -8, new Cesium.JulianDate());
      const ghost      = new Cesium.SampledPositionProperty();
      ghost.setInterpolationOptions({ interpolationDegree:3, interpolationAlgorithm:Cesium.HermitePolynomialApproximation });
      preds.forEach((p,i) => {
        const t = Cesium.JulianDate.addSeconds(ghostStart, i*secsPerLeg, new Cesium.JulianDate());
        ghost.addSample(t, Cesium.Cartesian3.fromDegrees(p.lng+0.3, p.lat+0.2, 55000+Math.sin(i*1.2+0.8)*15000));
      });
      tag(viewer.entities.add({
        availability: new Cesium.TimeIntervalCollection([new Cesium.TimeInterval({start:ghostStart,stop:ghostStop})]),
        position: ghost,
        billboard: { image:createUFOIcon(), width:40, height:20,
          color: Cesium.Color.fromCssColorString('#ffaa00').withAlpha(0.4),
          verticalOrigin: Cesium.VerticalOrigin.BOTTOM,
          disableDepthTestDistance: Number.POSITIVE_INFINITY,
        },
        path: { resolution:1, material: Cesium.Color.fromCssColorString('#ffaa00').withAlpha(0.2), width:2, leadTime:0, trailTime:12 },
      }));

      // Animate clock
      viewer.clock.startTime=startTime; viewer.clock.stopTime=stopTime;
      viewer.clock.currentTime=Cesium.JulianDate.clone(startTime);
      viewer.clock.clockRange=Cesium.ClockRange.LOOP_STOP;
      viewer.clock.multiplier=1; viewer.clock.shouldAnimate=true;

      // Fly to midpoint overview then zoom to first node
      const mid = preds[Math.floor(preds.length/2)];
      viewer.camera.flyTo({
        destination: Cesium.Cartesian3.fromDegrees(mid.lng, mid.lat, 10000000),
        orientation: { heading:0, pitch:Cesium.Math.toRadians(-45), roll:0 },
        duration: 2.5,
        complete: () => {
          setTimeout(() => {
            if (!viewer.isDestroyed()) viewer.camera.flyTo({
              destination: Cesium.Cartesian3.fromDegrees(preds[0].lng, preds[0].lat, 3000000),
              orientation: { heading:0, pitch:Cesium.Math.toRadians(-50), roll:0 },
              duration: 3.0,
            });
          }, 4000);
        },
      });
    },
  }));

  useEffect(() => {
    if (!containerRef.current || !CESIUM_TOKEN) return;
    let destroyed = false;

    if (!document.getElementById('cesium-css')) {
      const link = document.createElement('link');
      link.id='cesium-css'; link.rel='stylesheet';
      link.href=`${CESIUM_CDN}/Widgets/widgets.css`;
      document.head.appendChild(link);
    }

    const loadScript = () => new Promise((resolve, reject) => {
      if (window.Cesium) { resolve(); return; }
      const s = document.createElement('script');
      s.src=`${CESIUM_CDN}/Cesium.js`; s.async=true;
      s.onload=resolve; s.onerror=reject;
      document.head.appendChild(s);
    });

    const initViewer = async () => {
      try {
        await loadScript();
        if (destroyed||!containerRef.current) return;

        const Cesium = window.Cesium;
        Cesium.Ion.defaultAccessToken = CESIUM_TOKEN;

        let terrain;
        try {
          terrain = await Cesium.createWorldTerrainAsync({ requestWaterMask:true, requestVertexNormals:true });
        } catch(_) { terrain = new Cesium.EllipsoidTerrainProvider(); }
        if (destroyed) return;

        let imagery = null;
        try { imagery = await Cesium.IonImageryProvider.fromAssetId(3954); console.log('✅ Bing imagery');
        } catch(_) {
          try { imagery = await Cesium.IonImageryProvider.fromAssetId(2); console.log('✅ Cesium World Imagery');
          } catch(_2) {
            imagery = new Cesium.OpenStreetMapImageryProvider({ url:'https://tile.openstreetmap.org/', credit:'' });
            console.log('✅ OSM fallback');
          }
        }
        if (destroyed) return;

        const viewer = new Cesium.Viewer(containerRef.current, {
          imageryProvider:      imagery,
          terrainProvider:      terrain,
          animation:            false, baseLayerPicker:false, fullscreenButton:false,
          geocoder:             false, homeButton:false, infoBox:false,
          sceneModePicker:      false, selectionIndicator:false, timeline:false,
          navigationHelpButton: false, navigationInstructionsInitiallyVisible:false,
          scene3DOnly:          true, shadows:false, msaaSamples:4,
          requestRenderMode:    false,
          skyAtmosphere: new Cesium.SkyAtmosphere(),
          skyBox: new Cesium.SkyBox({ sources:{
            positiveX:`${CESIUM_CDN}/Assets/Textures/SkyBox/tycho2t3_80_px.jpg`,
            negativeX:`${CESIUM_CDN}/Assets/Textures/SkyBox/tycho2t3_80_mx.jpg`,
            positiveY:`${CESIUM_CDN}/Assets/Textures/SkyBox/tycho2t3_80_py.jpg`,
            negativeY:`${CESIUM_CDN}/Assets/Textures/SkyBox/tycho2t3_80_my.jpg`,
            positiveZ:`${CESIUM_CDN}/Assets/Textures/SkyBox/tycho2t3_80_pz.jpg`,
            negativeZ:`${CESIUM_CDN}/Assets/Textures/SkyBox/tycho2t3_80_mz.jpg`,
          }}),
        });
        if (destroyed) { viewer.destroy(); return; }

        viewer.scene.globe.enableLighting       = true;
        viewer.scene.globe.showGroundAtmosphere = true;
        viewer.scene.fog.enabled                = true;
        viewer.scene.fog.density                = 0.0002;
        viewer.scene.highDynamicRange           = true;
        viewer.scene.fxaa                       = true;
        viewer.scene.globe.maximumScreenSpaceError = 1.5;

        // Google Photorealistic 3D Tiles
        try {
          const tileset = await Cesium.Cesium3DTileset.fromIonAssetId(2275207);
          viewer.scene.primitives.add(tileset);
          console.log('✅ Google 3D Tiles');
        } catch(e) { console.warn('3D Tiles unavailable:', e.message); }

        viewer.clock.currentTime   = Cesium.JulianDate.now();
        viewer.clock.shouldAnimate = true;
        viewer.clock.multiplier    = 1;

        viewer.camera.setView({
          destination: Cesium.Cartesian3.fromDegrees(-40, 20, 22000000),
          orientation: { heading:0, pitch:Cesium.Math.toRadians(-90), roll:0 },
        });

        // Auto-rotate
        let autoRotate = true;
        const stopRot = () => { autoRotate = false; };
        viewer.scene.canvas.addEventListener('mousedown',  stopRot);
        viewer.scene.canvas.addEventListener('touchstart', stopRot);
        viewer.scene.postUpdate.addEventListener(() => {
          if (autoRotate) viewer.scene.camera.rotate(Cesium.Cartesian3.UNIT_Z, -0.00008);
        });

        // Incident markers — always visible
        (sightings||[]).forEach(inc => {
          const hex = inc.threatLevel==='RED' ? '#ff4545' : inc.threatLevel==='AMBER' ? '#ffaa00' : '#1de050';
          const ent = viewer.entities.add({
            id:       inc.id,
            position: Cesium.Cartesian3.fromDegrees(inc.lng, inc.lat, 500),
            billboard: {
              image:    createAlienPin(hex),
              width:44, height:54,
              verticalOrigin:  Cesium.VerticalOrigin.BOTTOM,
              heightReference: Cesium.HeightReference.NONE,
              disableDepthTestDistance: Number.POSITIVE_INFINITY,
              scaleByDistance:        new Cesium.NearFarScalar(1.0e3,1.5,2.0e7,0.6),
              translucencyByDistance: new Cesium.NearFarScalar(1.0e3,1.0,2.0e7,0.85),
            },
          });
          ent._admData = inc;
        });

        // Prediction rings + UFO saucers
        (predictions||[]).forEach(pred => {
          viewer.entities.add({
            position: Cesium.Cartesian3.fromDegrees(pred.lng, pred.lat, 100),
            ellipse: {
              semiMinorAxis:80000, semiMajorAxis:80000,
              material:     Cesium.Color.fromCssColorString('#1de050').withAlpha(0.12),
              outline:true, outlineColor:Cesium.Color.fromCssColorString('#1de050').withAlpha(0.5),
              outlineWidth:2,
            },
          });
          viewer.entities.add({
            position: Cesium.Cartesian3.fromDegrees(pred.lng, pred.lat, 80000),
            billboard: {
              image:    createUFOIcon(),
              width:56, height:28,
              verticalOrigin: Cesium.VerticalOrigin.BOTTOM,
              disableDepthTestDistance: Number.POSITIVE_INFINITY,
              scaleByDistance:        new Cesium.NearFarScalar(1.0e3,1.4,2.0e7,0.5),
              translucencyByDistance: new Cesium.NearFarScalar(1.0e3,1.0,2.0e7,0.8),
            },
          });
        });

        // Click handler
        const handler = new Cesium.ScreenSpaceEventHandler(viewer.scene.canvas);
        handler.setInputAction(click => {
          const picked = viewer.scene.pick(click.position);
          if (Cesium.defined(picked) && picked.id && picked.id._admData) {
            autoRotate = false;
            onSelect(picked.id._admData);
          } else { onSelect(null); }
        }, Cesium.ScreenSpaceEventType.LEFT_CLICK);

        if (onZoomLevel) {
          let lastZoom = -1;
          viewer.scene.postRender.addEventListener(() => {
            const altKm   = viewer.camera.positionCartographic.height / 1000;
            const zoom    = altKm>5000?1:altKm>1000?2:altKm>200?4:altKm>50?6:altKm>10?9:12;
            // Only fire when zoom level actually changes — not every frame
            if (zoom !== lastZoom) { lastZoom = zoom; onZoomLevel(zoom, 0, 0); }
          });
        }

        viewerRef.current = viewer;
        console.log('✅ Cesium ready');
      } catch(err) {
        console.error('Cesium init failed:', err);
        if (onError) onError();
      }
    };

    initViewer();
    return () => {
      destroyed = true;
      if (viewerRef.current && !viewerRef.current.isDestroyed()) {
        viewerRef.current.destroy(); viewerRef.current = null;
      }
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [CESIUM_TOKEN]);

  return (
    <div ref={containerRef} style={{width:'100%',height:'100%',background:'#07090c'}}>
      <style>{`
        .cesium-widget-credits,.cesium-credit-logoContainer,
        .cesium-credit-textContainer,.cesium-credit-expand-link{display:none!important;}
        .cesium-widget canvas{outline:none;}
      `}</style>
    </div>
  );
}

// ── UFO saucer canvas ─────────────────────────────────────────────────────────
function createUFOIcon() {
  const W=112,H=56,canvas=document.createElement('canvas');
  canvas.width=W; canvas.height=H;
  const ctx=canvas.getContext('2d');
  const glow=ctx.createRadialGradient(W/2,H*0.75,0,W/2,H*0.75,30);
  glow.addColorStop(0,'rgba(29,224,80,0.5)'); glow.addColorStop(1,'rgba(29,224,80,0)');
  ctx.fillStyle=glow; ctx.beginPath(); ctx.ellipse(W/2,H*0.75,30,14,0,0,Math.PI*2); ctx.fill();
  ctx.shadowColor='rgba(120,160,255,0.6)'; ctx.shadowBlur=16;
  const bodyGrd=ctx.createLinearGradient(0,H*0.45,0,H*0.75);
  bodyGrd.addColorStop(0,'rgba(210,225,245,0.95)'); bodyGrd.addColorStop(1,'rgba(155,175,210,0.85)');
  ctx.fillStyle=bodyGrd; ctx.beginPath(); ctx.ellipse(W/2,H*0.62,W*0.44,H*0.22,0,0,Math.PI*2); ctx.fill();
  ctx.shadowBlur=0;
  const domeGrd=ctx.createRadialGradient(W*0.42,H*0.35,0,W/2,H*0.45,W*0.18);
  domeGrd.addColorStop(0,'rgba(220,240,255,0.85)'); domeGrd.addColorStop(1,'rgba(160,205,255,0.5)');
  ctx.fillStyle=domeGrd; ctx.beginPath(); ctx.ellipse(W/2,H*0.45,W*0.18,H*0.28,0,Math.PI,Math.PI*2); ctx.fill();
  for(let i=0;i<8;i++){
    const angle=(i/8)*Math.PI, px=W/2+Math.cos(angle)*W*0.35, py=H*0.62+Math.sin(angle)*H*0.12;
    ctx.fillStyle=['rgba(0,220,255,0.9)','rgba(255,220,0,0.9)','rgba(0,255,120,0.9)'][i%3];
    ctx.beginPath(); ctx.arc(px,py,2.5,0,Math.PI*2); ctx.fill();
  }
  return canvas.toDataURL();
}

// ── Alien head pin canvas ─────────────────────────────────────────────────────
function createAlienPin(hexColor) {
  const W=76,H=92,canvas=document.createElement('canvas');
  canvas.width=W; canvas.height=H;
  const ctx=canvas.getContext('2d');
  ctx.shadowColor='rgba(0,0,0,0.6)'; ctx.shadowBlur=10; ctx.shadowOffsetY=4;
  ctx.beginPath(); ctx.arc(W/2,W/2,W/2-5,0,Math.PI*2);
  ctx.fillStyle='rgba(6,10,14,0.94)'; ctx.fill();
  ctx.strokeStyle=hexColor; ctx.lineWidth=3; ctx.stroke(); ctx.shadowBlur=0;
  const glow=ctx.createRadialGradient(W/2,W/2,0,W/2,W/2,W/2);
  glow.addColorStop(0,hexColor+'22'); glow.addColorStop(1,'transparent');
  ctx.fillStyle=glow; ctx.beginPath(); ctx.arc(W/2,W/2,W/2-5,0,Math.PI*2); ctx.fill();
  ctx.fillStyle=hexColor; ctx.shadowColor=hexColor; ctx.shadowBlur=8;
  ctx.beginPath(); ctx.ellipse(W/2,W/2-2,15,19,0,0,Math.PI*2); ctx.fill(); ctx.shadowBlur=0;
  ctx.fillStyle='rgba(3,7,5,0.97)';
  ctx.beginPath(); ctx.ellipse(W/2-6,W/2-5,5.5,3.8,-0.38,0,Math.PI*2); ctx.fill();
  ctx.beginPath(); ctx.ellipse(W/2+6,W/2-5,5.5,3.8,0.38,0,Math.PI*2); ctx.fill();
  ctx.fillStyle=hexColor; ctx.shadowColor='rgba(0,0,0,0.5)'; ctx.shadowBlur=6;
  ctx.beginPath(); ctx.moveTo(W/2-7,W-16); ctx.lineTo(W/2+7,W-16); ctx.lineTo(W/2,H-4); ctx.closePath(); ctx.fill();
  ctx.shadowBlur=0;
  return canvas.toDataURL();
}

// ── Loading screen ────────────────────────────────────────────────────────────
function GlobeLoader() {
  return (
    <div style={{position:'absolute',inset:0,background:'#07090c',display:'flex',flexDirection:'column',alignItems:'center',justifyContent:'center',gap:18,zIndex:5}}>
      <div style={{position:'relative',width:80,height:80}}>
        {[0,1,2].map(i=>(
          <div key={i} style={{position:'absolute',inset:i*8,borderRadius:'50%',border:`1.5px solid rgba(29,224,80,${0.5-i*0.12})`,animation:`adm-spin ${1.5+i*0.4}s linear infinite`}}/>
        ))}
        <div style={{position:'absolute',inset:24,borderRadius:'50%',background:'rgba(29,224,80,0.15)',display:'flex',alignItems:'center',justifyContent:'center',fontSize:18}}>🌍</div>
      </div>
      <div style={{fontSize:14,color:'rgba(140,180,150,0.7)',letterSpacing:1}}>Loading Earth...</div>
      <div style={{fontSize:11,color:'rgba(100,140,115,0.4)'}}>Cesium Ion · Photorealistic 3D</div>
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════════════
// MAIN EXPORT
// ══════════════════════════════════════════════════════════════════════════════
const GlobeCanvas = forwardRef(function GlobeCanvas(props, ref) {
  const [useCesium, setUseCesium] = useState(!!CESIUM_TOKEN);
  const [ready,     setReady]     = useState(false);
  const innerRef = useRef(null);

  useImperativeHandle(ref, () => ({
    zoomTo:               (...a) => innerRef.current?.zoomTo(...a),
    resetView:            ()     => innerRef.current?.resetView(),
    getCurrentZoom:       ()     => innerRef.current?.getCurrentZoom() ?? 1,
    showPredictionPath:   (...a) => innerRef.current?.showPredictionPath(...a),
  }));

  useEffect(() => {
    const t = setTimeout(() => setReady(true), useCesium ? 4000 : 800);
    return () => clearTimeout(t);
  }, [useCesium]);

  return (
    <div style={{width:'100%',height:'100%',position:'relative'}}>
      {!ready && <GlobeLoader/>}
      {useCesium
        ? <CesiumGlobe {...props} globeRef={innerRef} onError={()=>{setUseCesium(false);setReady(true);}}/>
        : <Canvas2DGlobe {...props} globeRef={innerRef}/>
      }
    </div>
  );
});

export default GlobeCanvas;
